import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query. Returns true when the viewport matches.
 * Re-subscribes if the query string changes.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Safari < 14 only supports addListener; we use addEventListener which
    // covers everything from 2020 onward.
    mql.addEventListener("change", handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

// Phone-and-small-tablet breakpoint used across the Ask AI surface.
export const useIsMobile = (): boolean =>
  useMediaQuery("(max-width: 768px)");

// Narrow tablet — useful for layouts that need a single intermediate step.
export const useIsNarrow = (): boolean =>
  useMediaQuery("(max-width: 1024px)");
